import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { uploadMediaFile, generateVideoPosterUrl, resolveMediaUrl } from "@/components/FileUpload";
import { uploadVideoNativeBackground, isNativePlatform } from "@/lib/nativeVideoUpload";
import { prepareStoryMediaUrl, trimPayload } from "@/lib/storyPublish";
import { Loader2, X, RotateCw, Check, Image as ImageIcon, Film, Music, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Global background-upload queue (Facebook-style): the composer hands off a job
// and closes immediately; uploads + the create request run here, surviving the
// composer unmount and page navigation. Feed jobs render as a banner at the top
// of the feed; story jobs render as a "Partage en cours…" tile in the story bar.
const Ctx = createContext(null);

// Reads an audio duration (seconds) from a File/blob/URL. Blob audio often reports
// Infinity on loadedmetadata, so we also wait for durationchange.
const readAudioDuration = (src) => new Promise((resolve) => {
  if (!src) { resolve(null); return; }
  const el = document.createElement("audio");
  el.preload = "metadata";
  let done = false;
  const finish = (v) => {
    if (done) return; done = true;
    el.removeEventListener("loadedmetadata", h);
    el.removeEventListener("durationchange", h);
    resolve(v);
  };
  const h = () => { if (isFinite(el.duration) && el.duration > 0) finish(el.duration); };
  el.addEventListener("loadedmetadata", h);
  el.addEventListener("durationchange", h);
  el.addEventListener("error", () => finish(null));
  setTimeout(() => finish(isFinite(el.duration) && el.duration > 0 ? el.duration : null), 3000);
  el.src = src;
});

export const useUploadQueue = () => useContext(Ctx) || {
  jobs: [], enqueuePost: () => {}, enqueueStory: () => {},
  cancelJob: () => {}, retryJob: () => {}, dismissJob: () => {},
};

let seq = 0;
const newJobId = () => `upjob-${Date.now()}-${seq++}`;
const makePreview = (file) => { try { return file ? URL.createObjectURL(file) : null; } catch { return null; } };

// Human-readable upload speed (e.g. "2,3 Mo/s") and ETA (e.g. "~1 min 20 s")
// for the background native video import banner.
const formatSpeed = (bps) => {
  if (!bps || bps <= 0) return "";
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1).replace(".", ",")} Mo/s`;
  if (bps >= 1024) return `${Math.round(bps / 1024)} Ko/s`;
  return `${Math.round(bps)} o/s`;
};
const formatEta = (sec) => {
  if (sec == null || sec < 0) return "";
  if (sec < 60) return `~${sec} s`;
  const m = Math.floor(sec / 60), s = sec % 60;
  return s ? `~${m} min ${s} s` : `~${m} min`;
};
// Durée d'une vidéo « 2:29 » / « 1:02:05 » (badge de la carte d'envoi).
const formatDuration = (s) => {
  if (s == null || !isFinite(s) || s <= 0) return null;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

// Failed sends survive an app close/reload ONLY when their media already reached
// the server (a serialisable retry payload exists) — raw File handles cannot be
// persisted by the browser. Mid-upload failures rely on live auto-resume instead.
const PERSIST_KEY = "yalola.upload_failed_jobs";
const loadPersisted = () => { try { return JSON.parse(localStorage.getItem(PERSIST_KEY) || "[]"); } catch { return []; } };
const savePersisted = (arr) => { try { localStorage.setItem(PERSIST_KEY, JSON.stringify(arr)); } catch { /* quota/full */ } };

// Lot 704 — envoi interrompu (app fermée / tuée en arrière-plan pendant une vidéo native) : un envoi dont chaque média
// est soit déjà sur le serveur (url), soit un fichier natif (chemin) est reconstituable → il survit au redémarrage et
// repart seul. Une photo web pas encore envoyée (File) ne l'est pas → null (comportement d'avant).
export const serializeJobInput = (input) => {
  const atts = input?.attachments || [];
  if (!atts.length || !atts.every((a) => a.url || a.nativeVideo?.path)) return null;
  const { previewFile: _f, nativePosterBlob: _b, ...rest } = input;
  return {
    ...rest,
    attachments: atts.map(({ file: _file, nativeVideo: nv, ...a }) => ({
      ...a,
      nativeVideo: nv && !a.url ? { path: nv.path, name: nv.name, size: nv.size, mimeType: nv.mimeType, width: nv.width, height: nv.height, duration: nv.duration } : null,
    })),
  };
};

// Tiny JPEG data-URL thumbnail (survives reload) for the "Réessayer" banner preview.
const makeImageThumb = (file) => new Promise((res) => {
  if (!file || !file.type?.startsWith("image")) { res(null); return; }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    try {
      const S = 96;
      const r = Math.min(S / img.width, S / img.height, 1);
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(img.width * r));
      c.height = Math.max(1, Math.round(img.height * r));
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      res(c.toDataURL("image/jpeg", 0.6));
    } catch { res(null); }
    finally { URL.revokeObjectURL(url); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); res(null); };
  img.src = url;
});

// First-frame JPEG data-URL for videos — WebView/Android paints a black <video>
// tile, so we grab a real still frame to show what's being shared.
// Résout { url, duration } : la durée lue ici sert de repli quand le composeur ne l'a pas.
const makeVideoThumb = (file) => new Promise((res) => {
  if (!file || !file.type?.startsWith("video")) { res(null); return; }
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.muted = true; v.playsInline = true; v.preload = "metadata"; v.src = url;
  let done = false;
  const dur = () => (isFinite(v.duration) && v.duration > 0 ? v.duration : null);
  const finish = (val) => { if (done) return; done = true; try { URL.revokeObjectURL(url); } catch { /* noop */ } res(val ? { url: val, duration: dur() } : (dur() ? { url: null, duration: dur() } : null)); };
  const grab = () => {
    try {
      const S = 320;
      const w = v.videoWidth, h = v.videoHeight;
      if (!w || !h) { finish(null); return; }
      const r = Math.min(S / w, S / h, 1);
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(w * r));
      c.height = Math.max(1, Math.round(h * r));
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      finish(c.toDataURL("image/jpeg", 0.72));
    } catch { finish(null); }
  };
  v.onloadeddata = () => { try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); } catch { grab(); } };
  v.onseeked = grab;
  v.onerror = () => finish(null);
  setTimeout(() => finish(null), 4000);
});

// Best still-frame preview for either media kind (data-URL → survives reload).
// Vidéo : { url, duration } ; photo : { url }.
const makeThumb = (file) => (file?.type?.startsWith("video") ? makeVideoThumb(file) : makeImageThumb(file).then((u) => (u ? { url: u } : null)));

export const UploadQueueProvider = ({ children }) => {
  const { config, t, user } = useApp();
  const [jobs, setJobs] = useState([]);
  const metaRef = useRef({}); // id -> { kind, input, previewUrl, controller, mediaKind, label, thumb, retryPayload, restored }
  // Publication programmée dont l'heure est dépassée pendant un envoi long (réseau lent) → publiée tout de suite.
  const postPayload = async (payload, signal) => {
    try {
      return await api.post("/posts", payload, { signal });
    } catch (e) {
      if (e?.response?.status === 400 && payload.scheduled_at && new Date(payload.scheduled_at).getTime() <= Date.now() + 60000) {
        const { scheduled_at: _drop, ...now } = payload;
        return await api.post("/posts", now, { signal });
      }
      throw e;
    }
  };

  const patch = useCallback((id, updates) => {
    setJobs((arr) => arr.map((j) => (j.id === id ? { ...j, ...(typeof updates === "function" ? updates(j) : updates) } : j)));
  }, []);

  const unpersist = useCallback((id) => {
    savePersisted(loadPersisted().filter((r) => r.id !== id));
  }, []);

  const persistFailure = useCallback((id) => {
    const m = metaRef.current[id];
    if (!m || !m.retryPayload) return; // only media-already-uploaded jobs can be retried after reload
    const rec = { id, kind: m.kind, mediaKind: m.mediaKind || "text", label: m.label || "", thumb: m.thumb || null, payload: m.retryPayload };
    const list = loadPersisted().filter((r) => r.id !== id);
    list.push(rec);
    savePersisted(list);
  }, []);

  // Lot 704 — instantané d'un envoi EN COURS (voir serializeJobInput) ; rappelé après chaque média envoyé (les URL
  // obtenues remplacent les File → l'album devient reconstituable dès que ses photos sont parties).
  const persistProgress = useCallback((id) => {
    const m = metaRef.current[id];
    if (!m || m.kind !== "post" || m.retryPayload) return;
    const input = serializeJobInput(m.input);
    const list = loadPersisted().filter((r) => r.id !== id);
    if (input) list.push({ id, kind: "post", mediaKind: m.mediaKind || "text", label: m.label || "", thumb: m.thumb || null, isVideo: !!m.isVideo, duration: m.duration ?? null, input });
    savePersisted(list);
  }, []);

  const removeJob = useCallback((id) => {
    setJobs((arr) => arr.filter((j) => j.id !== id));
    unpersist(id);
    const m = metaRef.current[id];
    if (m?.previewUrl) { try { URL.revokeObjectURL(m.previewUrl); } catch { /* noop */ } }
    delete metaRef.current[id];
  }, [unpersist]);

  const runPostJob = useCallback(async (id) => {
    const m = metaRef.current[id];
    if (!m) return;
    if (m.cancelled) { removeJob(id); return; }
    const controller = new AbortController();
    m.controller = controller;
    const signal = controller.signal;
    const { input } = m;
    patch(id, { status: "uploading", progress: 0, error: null });
    try {
      const pending = input.attachments.filter((a) => (a.file || a.nativeVideo) && !a.url);
      const total = pending.length || 1;
      const pct = {};
      // Multipart uploads fire dozens of progress events per second across 4
      // parallel parts — re-rendering the app on each one froze the UI during
      // big uploads. Only publish a state update when the integer % changes.
      let lastPct = -1;
      const report = (meta) => {
        const sum = pending.reduce((s, a, i) => s + (pct[i] || 0), 0);
        const p = Math.min(99, Math.round(sum / total));
        if (p === lastPct) return;
        lastPct = p;
        patch(id, meta === undefined ? { progress: p } : { progress: p, uploadMeta: meta });
      };
      // Envois en PARALLÈLE (3 à la fois : un album de 10 photos part ~3× plus vite) ; l'ordre d'ajout est conservé.
      // Une vidéo native (service Android) reste seule sur son créneau ; l'affiche d'une vidéo web est générée
      // PENDANT son envoi (plus après).
      const UPLOAD_PARALLEL = 3;
      const uploadOne = async (a) => {
        if (a.url) return { ...a };
        const i = pending.indexOf(a);
        if (a.nativeVideo) {
          const data = await uploadVideoNativeBackground({
            ...a.nativeVideo, context: "feed",
            isReel: !!input.isReel, compress: config?.settings?.features?.video_compression !== false, config,
            onProgress: (p, meta) => { pct[i] = p; report(meta || null); },
            signal,
          });
          return { ...a, url: data.url, poster_url: null, duration: data.duration || a.duration, width: data.width || a.width, height: data.height || a.height };
        }
        const posterP = a.kind === "video" ? generateVideoPosterUrl(a.file).catch(() => null) : Promise.resolve(null);
        const data = await uploadMediaFile(a.file, {
          config, context: "feed",
          onProgress: (p) => { pct[i] = p; report(); },
          signal,
        });
        const poster_url = await posterP;
        // width/height renvoyés par /upload (photos ET vidéos) → le fil réserve la place exacte (zéro saut).
        return { ...a, url: data.url, poster_url, duration: data.duration || a.duration, width: data.width || a.width, height: data.height || a.height };
      };
      const uploaded = new Array(input.attachments.length);
      let nextIdx = 0;
      const worker = async () => {
        for (;;) {
          const k = nextIdx++;
          if (k >= input.attachments.length) return;
          uploaded[k] = await uploadOne(input.attachments[k]);
          // Lot 704 — l'URL obtenue est reportée sur la pièce jointe d'origine : un « Réessayer » ou une reprise après
          // redémarrage ne renvoie pas ce média, et l'instantané persistant s'enrichit.
          Object.assign(input.attachments[k], uploaded[k]);
          persistProgress(id);
        }
      };
      await Promise.all(Array.from({ length: Math.min(UPLOAD_PARALLEL, input.attachments.length) }, worker));
      patch(id, { status: "posting", progress: 100, uploadMeta: null });
      const visuals = uploaded.filter((a) => a.kind === "image" || a.kind === "video");
      const images = visuals.filter((a) => a.kind === "image").map((a) => a.url);
      const videoAtt = visuals.find((a) => a.kind === "video");
      const audioAtt = uploaded.find((a) => a.kind === "audio");
      let audioDur = (audioAtt && isFinite(audioAtt.duration) && audioAtt.duration > 0) ? audioAtt.duration : null;
      if (audioAtt && !audioDur) {
        let src = null, revoke = false;
        if (audioAtt.file) { src = URL.createObjectURL(audioAtt.file); revoke = true; }
        else if (audioAtt.url) { src = resolveMediaUrl(audioAtt.url); }
        audioDur = await readAudioDuration(src);
        if (revoke && src) { try { URL.revokeObjectURL(src); } catch { /* noop */ } }
      }
      const payload = {
        content: input.content,
        images,
        group_id: input.groupId || null,
        ...(input.groupIds?.length ? { group_ids: input.groupIds } : {}), // lot 756 — publier dans plusieurs groupes
        audio_url: audioAtt?.url || null,
        audio_duration: audioDur ? Math.round(audioDur * 100) / 100 : null,
        bg: null,
      };
      const imageDims = Object.fromEntries(visuals.filter((a) => a.kind === "image" && a.width > 0 && a.height > 0).map((a) => [a.url, [a.width, a.height]]));
      if (Object.keys(imageDims).length) payload.image_dims = imageDims;
      if (videoAtt?.url) {
        payload.video_url = videoAtt.url;
        payload.is_reel = input.isReel && !(visuals.length > 1);
        if (videoAtt.poster_url) payload.poster_url = videoAtt.poster_url;
        if (videoAtt.fx) payload.video_fx = videoAtt.fx; // lot 534 : filtre vidéo appliqué en CSS à la lecture
        const vd = Number(videoAtt.duration);
        if (isFinite(vd) && vd > 0) payload.video_duration = Math.round(vd * 100) / 100;
      }
      // Ordered mixed-media album (photos + videos) — preserves the user's add order.
      if (visuals.length > 1) {
        payload.album = visuals.map((a) => ({
          type: a.kind, url: a.url,
          ...(a.poster_url ? { poster_url: a.poster_url } : {}),
          ...(a.width > 0 && a.height > 0 ? { width: a.width, height: a.height } : {}),
          ...(a.kind === "video" && isFinite(Number(a.duration)) && Number(a.duration) > 0 ? { duration: Math.round(Number(a.duration) * 100) / 100 } : {}),
          ...(a.kind === "video" && a.fx ? { fx: a.fx } : {}),
          ...(a.caption && a.caption.trim() ? { caption: a.caption.trim() } : {}),
        }));
        // « Avant / Après » : 2 photos ou 2 vidéos (1re = avant, 2e = après).
        if (input.album_layout === "before_after" && visuals.length === 2 && visuals[0].kind === visuals[1].kind) payload.album_layout = "before_after";
      }
      if (input.music?.url) {
        payload.music_url = input.music.url;
        payload.music_title = input.music.title || null;
        payload.music_keep_media_sound = !!input.music.keepVideoSound;
        if (typeof input.music.volume === "number") payload.music_volume = input.music.volume;
        if (input.music.start) payload.music_start = input.music.start;
      }
      if (input.church_id) payload.church_id = input.church_id;
      if (input.visibility) payload.visibility = input.visibility; // lot 649 — audience choisie
      if (Array.isArray(input.mentions) && input.mentions.length) payload.mentions = input.mentions;
      if (input.scheduled_at) payload.scheduled_at = input.scheduled_at; // publication programmée (média lourd envoyé en arrière-plan)
      if (input.post_type === "testimony") { payload.post_type = "testimony"; payload.testimony_category = input.testimony_category || "other"; }
      m.retryPayload = payload; // media is uploaded → this send survives a reload
      const { data } = await postPayload(payload, signal);
      unpersist(id); // publiée : plus rien à reprendre (évite un doublon si l'app est tuée dans les 2,5 s qui suivent)
      patch(id, { status: "done", progress: 100 });
      if (data?.scheduled) {
        window.dispatchEvent(new Event("yalola:post_scheduled")); // rafraîchit la file « Publications programmées »
        toast.success(t("post.scheduled_ok", "Publication programmée ✓ — elle sera publiée automatiquement."));
      } else {
        window.dispatchEvent(new CustomEvent("yalola:post_created", { detail: data?.post }));
        toast.success(input.isReel ? t("reel.published", "Reel publié ! 🎬") : t("post.published", "Publication envoyée !"),
          data?.moderation_pending ? { description: t("upload.moderation_pending", "Vérification automatique de vos photos en cours — vous serez averti(e) si l'une d'elles doit être retirée."), duration: 6000 } : undefined);
      }
      setTimeout(() => removeJob(id), 2500);
    } catch (e) {
      if (signal.aborted) { removeJob(id); return; } // user cancelled only; network drops → keep a retryable tile
      patch(id, { status: "error", error: e?.friendlyMessage || e?.message || "?", gated: !!e?.profileIncomplete });
      persistFailure(id);
      toast.error(t("composer.upload_error", "Échec de l'envoi"));
    }
  }, [config, t, patch, removeJob, persistFailure, persistProgress, unpersist]);

  const runStoryJob = useCallback(async (id) => {
    const m = metaRef.current[id];
    if (!m) return;
    if (m.cancelled) { removeJob(id); return; }
    const controller = new AbortController();
    m.controller = controller;
    const signal = controller.signal;
    const { input } = m;
    patch(id, { status: "uploading", progress: 0, error: null });
    let lastPhaseKey = ""; // dedupe progress events → avoid re-render storms
    const onPhase = (phase, p) => {
      const pr = Math.min(99, Math.round(p));
      const key = `${phase}:${pr}`;
      if (key === lastPhaseKey) return;
      lastPhaseKey = key;
      patch(id, {
        progress: pr,
        phaseLabel: phase === "trim" ? t("story.trimming", "Découpage") : t("story.uploading_clip", "Envoi"),
      });
    };
    try {
      const mm = input.media; // { file, url, media_type, poster_url }
      let media_url = null;
      let poster = null;
      let trimFields = {};
      if (mm.media_type !== "video") {
        if (mm.url) { media_url = mm.url; poster = mm.poster_url || null; }
        else {
          const data = await uploadMediaFile(mm.file, { config, context: "story", onProgress: (p) => onPhase("upload", p), signal });
          media_url = data.url;
        }
      } else if (mm.url) {
        media_url = mm.url; poster = mm.poster_url || null;
      } else {
        // Lot 613 — même préparation que la feuille PC : découpe locale, sinon la vidéo part entière et le serveur
        // coupe l'extrait choisi (trim_start / trim_duration) — iPhone compris.
        const m = await prepareStoryMediaUrl({ media: mm, config, t, ...input.trimOpts, signal, setProc: (p) => { if (p) onPhase(p.phase, p.pct); } });
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        media_url = m.url;
        poster = m.poster || null;
        trimFields = trimPayload(m);
      }
      patch(id, { status: "posting", progress: 100 });
      const payload = input.mode === "media"
        ? { media_url, media_type: mm.media_type, ...(poster ? { poster_url: poster } : {}), ...trimFields, ...input.church, ...input.extras, media_transform: input.transform, ...(input.stickers?.length ? { stickers: input.stickers } : {}), ...(input.embed?.url ? { embed: input.embed } : {}) }
        : { media_type: "text", text: input.text, bg: input.bg, ...(input.stickers?.length ? { stickers: input.stickers } : {}), ...input.church, ...input.extras };
      m.retryPayload = payload; // media is uploaded → this send survives a reload
      const { data: sdata } = await api.post("/stories", payload, { signal });
      patch(id, { status: "done", progress: 100 });
      window.dispatchEvent(new Event("yalola:story_created"));
      toast.success(sdata?.scheduled ? t("story.scheduled_ok", "Story programmée ✓ — elle sera publiée automatiquement.") : t("story.created", "Story publiée !"));
      setTimeout(() => removeJob(id), 2000);
    } catch (e) {
      if (signal.aborted) { removeJob(id); return; } // user cancelled only; network drops → keep a retryable tile
      patch(id, { status: "error", error: e?.friendlyMessage || e?.message || "?", gated: !!e?.profileIncomplete });
      persistFailure(id);
      toast.error(e?.friendlyMessage || t("composer.upload_error", "Échec de l'envoi"));
    }
  }, [config, t, patch, removeJob, persistFailure]);

  // Retry a job whose media already reached the server: just re-POST (no re-upload).
  // Used by the one-tap "Réessayer" on restored jobs and post-upload failures.
  const runFinalize = useCallback(async (id) => {
    const m = metaRef.current[id];
    if (!m || !m.retryPayload) return;
    if (m.cancelled) { removeJob(id); return; }
    const controller = new AbortController();
    m.controller = controller;
    patch(id, { status: "posting", progress: 100, error: null });
    try {
      const { data } = m.kind === "story"
        ? await api.post("/stories", m.retryPayload, { signal: controller.signal })
        : await postPayload(m.retryPayload, controller.signal);
      patch(id, { status: "done", progress: 100 });
      unpersist(id);
      window.dispatchEvent(m.kind === "story"
        ? new Event("yalola:story_created")
        : data?.scheduled ? new Event("yalola:post_scheduled") : new CustomEvent("yalola:post_created", { detail: data?.post }));
      toast.success(m.kind === "story"
        ? (data?.scheduled ? t("story.scheduled_ok", "Story programmée ✓ — elle sera publiée automatiquement.") : t("story.created", "Story publiée !"))
        : data?.scheduled ? t("post.scheduled_ok", "Publication programmée ✓ — elle sera publiée automatiquement.") : t("post.published", "Publication envoyée !"),
        data?.moderation_pending ? { description: t("upload.moderation_pending", "Vérification automatique de vos photos en cours — vous serez averti(e) si l'une d'elles doit être retirée."), duration: 6000 } : undefined);
      setTimeout(() => removeJob(id), m.kind === "story" ? 2000 : 2500);
    } catch (e) {
      if (controller.signal.aborted) { removeJob(id); return; } // user cancelled only
      patch(id, { status: "error", error: e?.friendlyMessage || e?.message || "?", gated: !!e?.profileIncomplete });
      persistFailure(id);
      toast.error(t("composer.upload_error", "Échec de l'envoi"));
    }
  }, [t, patch, removeJob, unpersist, persistFailure]);

  // Native video uploads run ONE AT A TIME so a single user's upstream bandwidth
  // isn't split across several big videos. Image/audio/web posts still run at once.
  const nativeQueueRef = useRef([]);    // post-job ids waiting for their native-upload turn
  const nativeActiveRef = useRef(null); // id currently uploading natively, or null
  const pumpNativeRef = useRef(() => {});
  const pumpNative = useCallback(() => {
    if (nativeActiveRef.current) return;
    let nextId = null;
    while (nativeQueueRef.current.length) {
      const cand = nativeQueueRef.current.shift();
      const mm = metaRef.current[cand];
      if (mm && !mm.cancelled) { nextId = cand; break; }
    }
    if (!nextId) return;
    nativeActiveRef.current = nextId;
    Promise.resolve(runPostJob(nextId)).finally(() => {
      nativeActiveRef.current = null;
      pumpNativeRef.current();
    });
  }, [runPostJob]);
  pumpNativeRef.current = pumpNative;

  const enqueueNative = useCallback((id) => {
    nativeQueueRef.current.push(id);
    // Anything already uploading → show a "waiting" state until this job's turn.
    if (nativeActiveRef.current && nativeActiveRef.current !== id) patch(id, { status: "queued", progress: 0 });
    pumpNativeRef.current();
  }, [patch]);

  const enqueuePost = useCallback((input) => {
    const id = newJobId();
    // Aperçu de la carte « Importation » : TOUJOURS une image. 1er visuel = photo → son blob ; vidéo native → l'image
    // fixe capturée par le composeur (nativePosterBlob) ; vidéo web → image extraite ensuite (makeThumb, data:).
    // Jamais l'URL du fichier vidéo : un <img> dessus affichait l'icône « image cassée » sur fond noir.
    const firstVisual = (input.attachments || []).find((a) => a.kind === "image" || a.kind === "video");
    const firstIsVideo = firstVisual ? firstVisual.kind === "video" : input.mediaKind === "video";
    const previewUrl = firstIsVideo ? makePreview(input.nativePosterBlob) : makePreview(input.previewFile);
    const label = (input.content || "").slice(0, 80);
    // Durée de la vidéo montrée dans la carte (badge « 2:29 ») : celle du composeur, sinon lue sur le fichier.
    const firstDur = firstIsVideo ? Number(firstVisual?.duration) : NaN;
    const duration = isFinite(firstDur) && firstDur > 0 ? firstDur : null;
    metaRef.current[id] = { kind: "post", input, previewUrl, controller: null, mediaKind: input.mediaKind, label, thumb: null, isVideo: firstIsVideo, duration };
    persistProgress(id); // lot 704 — vidéo native : reconstituable dès le départ
    makeThumb(input.previewFile || (firstIsVideo ? input.nativePosterBlob : null)).then((th) => {
      if (!metaRef.current[id] || !th) return;
      const upd = {};
      if (th.url) {
        metaRef.current[id].thumb = th.url;
        // A captured video frame (data: URL) is a safe <img> source.
        if (firstIsVideo && !previewUrl) { metaRef.current[id].previewUrl = th.url; upd.previewUrl = th.url; }
      }
      if (firstIsVideo && !duration && th.duration) { upd.duration = th.duration; metaRef.current[id].duration = th.duration; }
      if (Object.keys(upd).length) patch(id, upd);
      persistProgress(id); // la vignette rejoint l'instantané
    });
    const hasNativeVideo = (input.attachments || []).some((a) => a.nativeVideo && !a.url);
    setJobs((arr) => [...arr, {
      id, kind: "post",
      status: hasNativeVideo && nativeActiveRef.current ? "queued" : "uploading",
      progress: 0, phaseLabel: "",
      mediaKind: input.mediaKind, previewUrl, label, error: null,
      isVideo: firstIsVideo, duration,
    }]);
    if (hasNativeVideo) enqueueNative(id);
    else setTimeout(() => runPostJob(id), 0);
  }, [runPostJob, patch, enqueueNative, persistProgress]);

  const enqueueStory = useCallback((input) => {
    const id = newJobId();
    const previewUrl = input.mediaKind === "video" ? null : makePreview(input.previewFile);
    metaRef.current[id] = { kind: "story", input, previewUrl, controller: null, mediaKind: input.mediaKind, label: "", thumb: null };
    makeThumb(input.previewFile).then((th) => {
      if (!metaRef.current[id] || !th?.url) return;
      metaRef.current[id].thumb = th.url;
      if (input.mediaKind === "video") { metaRef.current[id].previewUrl = th.url; patch(id, { previewUrl: th.url }); }
    });
    setJobs((arr) => [...arr, {
      id, kind: "story", status: "uploading", progress: 0, phaseLabel: "",
      mediaKind: input.mediaKind, previewUrl, label: "", error: null,
    }]);
    setTimeout(() => runStoryJob(id), 0);
  }, [runStoryJob, patch]);

  const cancelJob = useCallback((id) => {
    nativeQueueRef.current = nativeQueueRef.current.filter((x) => x !== id);
    const m = metaRef.current[id];
    if (m) {
      m.cancelled = true; // covers the enqueue→first-tick race (controller not created yet)
      if (m.controller) { try { m.controller.abort(); } catch { /* noop */ } }
    }
    removeJob(id);
  }, [removeJob]);

  const retryJob = useCallback((id) => {
    const m = metaRef.current[id];
    if (!m) return;
    m.cancelled = false;
    // Media already uploaded (restored job or post-upload failure) → just re-POST.
    if (m.retryPayload) { runFinalize(id); return; }
    // A native video that still needs uploading goes back through the serial queue.
    const isNativeUpload = m.kind === "post" && (m.input?.attachments || []).some((a) => a.nativeVideo && !a.url);
    if (isNativeUpload) { enqueueNative(id); return; }
    patch(id, { status: "uploading", progress: 0, error: null });
    if (m.kind === "post") runPostJob(id); else runStoryJob(id);
  }, [patch, runPostJob, runStoryJob, runFinalize, enqueueNative]);

  // Keep a live ref so the (run-once) listeners below always call the latest retry.
  const retryRef = useRef(retryJob);
  retryRef.current = retryJob;
  const restoredRef = useRef(false);

  // Restore failed sends from a previous session + auto-resume when the network returns.
  useEffect(() => {
    if (!restoredRef.current) {
      restoredRef.current = true;
      const persisted = loadPersisted();
      if (persisted.length) {
        persisted.forEach((r) => {
          // Lot 704 — `input` = envoi interrompu reconstituable (médias natifs / déjà envoyés) ; `payload` = média déjà
          // sur le serveur, il ne reste qu'à publier.
          metaRef.current[r.id] = r.input
            ? { kind: "post", input: r.input, mediaKind: r.mediaKind, label: r.label, thumb: r.thumb, isVideo: !!r.isVideo, duration: r.duration ?? null, previewUrl: null, controller: null, restored: true }
            : { kind: r.kind, retryPayload: r.payload, mediaKind: r.mediaKind, label: r.label, thumb: r.thumb, previewUrl: null, controller: null, restored: true };
        });
        setJobs((arr) => [
          ...arr,
          ...persisted
            .filter((r) => !arr.some((j) => j.id === r.id))
            .map((r) => ({
              id: r.id, kind: r.kind, status: "error", progress: 0, phaseLabel: "",
              mediaKind: r.mediaKind, previewUrl: r.thumb || null, label: r.label || "", error: "", restored: true,
              interrupted: !!r.input, isVideo: !!r.isVideo, duration: r.duration ?? null,
            })),
        ]);
        // Envoi interrompu par la fermeture de l'app : il repart seul (ce qui est déjà sur le serveur n'est pas renvoyé).
        persisted.filter((r) => r.input).forEach((r) => setTimeout(() => retryRef.current(r.id), 1500));
      }
    }
    // Retry every errored job (e.g. a native video upload interrupted by a network
    // drop) as soon as connectivity is back or the app returns to the foreground.
    const resumeErrored = () => {
      setJobs((cur) => {
        cur.forEach((j) => { if (j.status === "error") setTimeout(() => retryRef.current(j.id), 400); });
        return cur;
      });
    };
    window.addEventListener("online", resumeErrored);
    let netHandle = null, appHandle = null;
    if (isNativePlatform()) {
      // Reliable native connectivity signal (window 'online' is flaky in a WebView).
      import("@capacitor/network").then(({ Network }) =>
        Network.addListener("networkStatusChange", (s) => { if (s?.connected) resumeErrored(); })
      ).then((h) => { netHandle = h; }).catch(() => { /* plugin missing */ });
      import("@capacitor/app").then(({ App }) =>
        App.addListener("appStateChange", (s) => { if (s?.isActive) resumeErrored(); })
      ).then((h) => { appHandle = h; }).catch(() => { /* noop */ });
    }
    return () => {
      window.removeEventListener("online", resumeErrored);
      try { netHandle?.remove?.(); } catch { /* noop */ }
      try { appHandle?.remove?.(); } catch { /* noop */ }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Facebook-style guard: warn before closing the tab while something is still
  // uploading (an interrupted upload publishes nothing and leaves orphan chunks).
  const hasActive = jobs.some((j) => j.status === "uploading" || j.status === "posting" || j.status === "queued");
  useEffect(() => {
    if (!hasActive) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasActive]);

  // Lot 597 — un envoi refusé pour profil incomplet (403 profile_incomplete) repart tout seul dès que le profil est
  // complété : plus de tuile « Échec — Réessayer » qui traîne après le formulaire.
  const profileComplete = user?.profile_completion?.complete !== false;
  const wasIncompleteRef = useRef(!profileComplete);
  useEffect(() => {
    if (profileComplete && wasIncompleteRef.current) {
      jobs.filter((j) => j.status === "error" && j.gated).forEach((j) => retryJob(j.id));
    }
    wasIncompleteRef.current = !profileComplete;
  }, [profileComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = React.useMemo(
    () => ({ jobs, enqueuePost, enqueueStory, cancelJob, retryJob, dismissJob: removeJob }),
    [jobs, enqueuePost, enqueueStory, cancelJob, retryJob, removeJob]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// --- Feed banner (top of the Fil): one card per in-progress post ---
export const FeedUploadBanner = () => {
  const { jobs, cancelJob, retryJob } = useUploadQueue();
  const { t } = useApp();
  const postJobs = jobs.filter((j) => j.kind === "post");
  if (postJobs.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="feed-upload-banner">
      {postJobs.map((j) => {
        // previewUrl est toujours une image (blob:/data:) depuis le lot 704 → affichable telle quelle.
        const strip = j.isVideo && !!j.previewUrl;
        const durStr = j.isVideo ? formatDuration(j.duration) : null;
        return (
        <div key={j.id} className="yalola-card overflow-hidden" data-testid={`feed-upload-job-${j.id}`}>
          {strip && (
            // Aperçu façon fil : image floue en fond, première image nette au centre, durée en badge.
            <div className="relative h-44 bg-black overflow-hidden" data-testid={`feed-upload-preview-${j.id}`}>
              <div className="absolute inset-0 pointer-events-none" aria-hidden
                style={{ backgroundImage: `url(${j.previewUrl})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(24px)", transform: "scale(1.18)", opacity: 0.85 }} />
              <img src={j.previewUrl} alt="" className="relative z-[1] w-full h-full object-contain" />
              <div className="absolute top-2 left-2 z-[2] flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-white pointer-events-none" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}>
                <Film size={11} /> {t("upload.video_label", "Vidéo")}
              </div>
              {durStr && (
                <div className="absolute bottom-2 left-2 z-[2] px-2 py-0.5 rounded text-[11px] font-semibold text-white pointer-events-none" style={{ background: "rgba(0,0,0,0.72)" }} data-testid={`feed-upload-duration-${j.id}`}>
                  {durStr}
                </div>
              )}
              {(j.status === "uploading" || j.status === "posting" || j.status === "queued") && (
                <div className="absolute bottom-2 right-2 z-[2] h-8 w-8 rounded-full grid place-items-center text-white" style={{ background: "rgba(0,0,0,0.55)" }} aria-hidden>
                  <Loader2 size={16} className="animate-spin" />
                </div>
              )}
            </div>
          )}
        <div className="p-3 flex items-center gap-3">
          {!strip && (
          <div className="h-12 w-12 rounded-lg overflow-hidden bg-black shrink-0 grid place-items-center">
            {j.previewUrl
              ? <img src={j.previewUrl} alt="" className="w-full h-full object-cover" />
              : j.mediaKind === "video"
                ? <Film size={20} className="text-white/70" />
                : j.mediaKind === "audio"
                  ? <Music size={20} className="text-white/70" />
                  : <ImageIcon size={20} className="text-white/70" />}
          </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid={`feed-upload-status-${j.id}`}>
              {j.status === "error"
                ? (j.interrupted && !j.error ? t("upload.interrupted", "Envoi interrompu — reprise…") : t("composer.upload_error", "Échec de l'envoi"))
                : j.status === "done"
                  ? t("post.published", "Publication envoyée !")
                  : j.status === "queued"
                    ? t("upload.queued", "En attente…")
                    : j.status === "posting"
                      ? t("composer.processing", "Traitement...")
                      : `${j.uploadMeta?.phase === "compress" ? t("upload.compressing", "Compression") : t("upload.importing", "Importation")} · ${j.progress}%`
                        + (j.uploadMeta?.bytesPerSec ? ` · ${formatSpeed(j.uploadMeta.bytesPerSec)}` : "")
                        + (j.uploadMeta?.etaSec != null ? ` · ${formatEta(j.uploadMeta.etaSec)}` : "")}
            </div>
            <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${j.progress}%`, background: j.status === "error" ? "#ef4444" : "var(--yalola-primary)" }} />
            </div>
            {(j.status === "uploading" || j.status === "posting" || j.status === "queued") && (
              <div className="mt-1 text-[11px] flex items-center gap-1 font-medium" style={{ color: "#B45309" }} data-testid={`feed-upload-warning-${j.id}`}>
                <AlertTriangle size={11} className="shrink-0" />
                <span className="truncate">{t("upload.dont_close", "Ne fermez pas l'application pendant l'envoi")}</span>
              </div>
            )}
          </div>
          {j.status === "error" ? (
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => retryJob(j.id)} className="pill pill-primary !px-3 !py-1.5 !text-xs inline-flex items-center gap-1" data-testid={`feed-upload-retry-${j.id}`}>
                <RotateCw size={13} /> {t("composer.retry", "Réessayer")}
              </button>
              <button onClick={() => cancelJob(j.id)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" data-testid={`feed-upload-dismiss-${j.id}`}>
                <X size={16} />
              </button>
            </div>
          ) : j.status === "done" ? (
            <Check size={18} className="text-emerald-600 shrink-0" data-testid={`feed-upload-done-${j.id}`} />
          ) : (
            <button onClick={() => cancelJob(j.id)} className="pill pill-outline !px-3 !py-1.5 !text-xs shrink-0" data-testid={`feed-upload-cancel-${j.id}`}>
              {t("common.cancel", "Annuler")}
            </button>
          )}
        </div>
        </div>
        );
      })}
    </div>
  );
};

// --- Story bar tiles: one "Partage en cours…" tile per in-progress story ---
export const StoryUploadTiles = () => {
  const { jobs, cancelJob, retryJob } = useUploadQueue();
  const { t } = useApp();
  const storyJobs = jobs.filter((j) => j.kind === "story");
  if (storyJobs.length === 0) return null;
  return storyJobs.map((j) => (
    <div key={j.id} className="story-tile story-tile-new" data-testid={`story-uploading-${j.id}`}>
      {j.previewUrl && (j.mediaKind !== "video" || j.previewUrl.startsWith("data:"))
        ? <img src={j.previewUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        : (j.mediaKind === "video" || j.mediaKind === "audio")
          ? <div className="absolute inset-0 grid place-items-center" style={{ background: "var(--gradient-navy)" }}>{j.mediaKind === "audio" ? <Music size={22} className="text-white/80" /> : <Film size={22} className="text-white/80" />}</div>
          : <div className="absolute inset-0" style={{ background: "var(--gradient-navy)" }} />}
      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1 px-1 text-center">
        {j.status === "error" ? (
          <>
            <span className="text-white text-[10px] font-semibold leading-tight">{t("composer.upload_error", "Échec de l'envoi")}</span>
            <div className="flex items-center gap-1 mt-0.5">
              <button onClick={() => retryJob(j.id)} className="rounded-full bg-white/90 px-2 py-0.5 text-[9px] font-bold inline-flex items-center gap-0.5" style={{ color: "var(--yalola-primary)" }} data-testid={`story-uploading-retry-${j.id}`}>
                <RotateCw size={9} /> {t("composer.retry", "Réessayer")}
              </button>
              <button onClick={() => cancelJob(j.id)} className="h-5 w-5 grid place-items-center rounded-full bg-black/50 text-white" data-testid={`story-uploading-cancel-${j.id}`}>
                <X size={11} />
              </button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="animate-spin text-white" size={20} />
            <span className="text-white text-[10px] font-semibold leading-tight">
              {j.status === "posting" ? t("composer.processing", "Traitement…") : t("upload.importing", "Importation")}
            </span>
            {j.status !== "posting" && <span className="text-white text-[10px] font-medium">{j.progress}%</span>}
            <button onClick={() => cancelJob(j.id)} className="mt-0.5 text-white/90 text-[9px] underline" data-testid={`story-uploading-cancel-${j.id}`}>
              {t("common.cancel", "Annuler")}
            </button>
          </>
        )}
      </div>
      <div className="story-tile-name line-clamp-2 leading-tight">{t("story.your_story", "Votre story")}</div>
    </div>
  ));
};
